import * as React from 'react';
import { useState, useCallback, useEffect } from 'react';
import { notification } from 'antd';
import Parameters from '../parameters';
import API_SERVICE from '../services/api-service';
import { useTranslation } from 'react-i18next';
import { getImageUrl } from '../services/Utility';

let AUTH_TOKEN: any = null;
let listener: any = null;

interface AuthContextProps {
    authToken: any | null;
    user: any | undefined;
    isAuth: boolean;
    cart: any[];
    cartTime: string;
    updateAuthToken: (authToken: any | null) => void;
    updateCartTime: (time: any | null) => void;
    updateCart: (items: any[]) => void;
    updateUser: () => void;
}


const AuthContext = React.createContext<AuthContextProps>({
    authToken: null,
    user: undefined,
    isAuth: false,
    cart: [],
    cartTime: '',
    updateAuthToken: () => {
    },
    updateCartTime: () => {
    },
    updateCart: () => {
    },
    updateUser: () => {
    },
});

function AuthProvider({ children }: any) {
    const { t } = useTranslation();
    const [authToken, setAuthToken] = useState(() => {
        const authTokenStr = localStorage.getItem('auth-token');
        const lAuthToken = authTokenStr ? JSON.parse(authTokenStr) : undefined;
        AUTH_TOKEN = lAuthToken;
        return lAuthToken;
    });

    const [cartTime, setCartTime] = useState('');
    const [cart, setCart] = useState([] as any[]);
    const [user, setUser] = useState<any | undefined>(
        authToken && authToken.user,
    );

    const updateAuthToken = useCallback((pAuthToken: any) => {
        if (pAuthToken) {
            setUser(pAuthToken.user);
            localStorage.setItem('auth-token', JSON.stringify(pAuthToken));
        } else {
            localStorage.removeItem('auth-token');
        }
        AUTH_TOKEN = pAuthToken;
        setAuthToken(pAuthToken);
    }, []);
    const updateCartTime = useCallback((time: any) => {
        setCartTime(time);
    }, []);

    const updateCart = useCallback((items: any[]) => {
        setCart(JSON.parse(JSON.stringify(items)));
    }, []);
    useEffect(() => {
        if (authToken) {
            updateUser();
        }
    }, []);
    const updateUser = useCallback(() => {
        API_SERVICE.me().then(({ data: { data: u } }) => {
            const { user: innerU } = u;
            const uFinal = innerU || u;
            setUser(uFinal);
        }).catch((e: any) => {
            localStorage.removeItem('auth-token');
            window.location.href = '/login';
            notification.error({
                message: API_SERVICE.handleErrors(e),
            });
        });
    }, []);
    const fetchCart = () => {
        if (authToken && Parameters.Mode === 'CLIENT') {
            API_SERVICE.fetchCart().then((c) => {
                const count = c.data.data.items.length;
                updateCart(c.data.data.items);
            }).catch((e) => {
                notification.error({
                    message: API_SERVICE.handleErrors(e),
                });
            });
        }
    };

    useEffect(() => {
        fetchCart();
    }, [cartTime, authToken]);

    useEffect(() => {
        function forceLogout() {
            notification.error({
                message: 'You are logged Out',
            });
            updateAuthToken(null);
        }
    }, [authToken]);

    return (
        <AuthContext.Provider
            value={{
                updateCartTime,
                updateCart,
                cart,
                cartTime,
                updateAuthToken,
                authToken,
                updateUser,
                user,
                isAuth: !!authToken,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

const AuthConsumer = AuthContext.Consumer;

/**
 * We use this function to be able to access
 * the auth token from outside React components.
 */
function getAuthToken() {
    return AUTH_TOKEN;
}

function getUrlFormattedToken() {
    const token = getAuthToken();
    return token ? encodeURIComponent(token.value) : '';
}

function logout() {
    if (listener != null) {
        listener(null);
    }
    localStorage.clear();
    window.location.href = '/login';
    notification.error({ message: 'You are logged out' });
}

export {
    AuthProvider,
    AuthConsumer,
    AuthContext,
    getAuthToken,
    getUrlFormattedToken,
    logout,
};
